import {
  LeadSystemSource,
  MixpanelClient,
  MixpanelEvent,
  ProgramType,
  scheduleClient,
  SchedulingWorkflow,
  Step,
  User,
} from '@enaratech/funnel-helper';
import { Dispatch } from 'react';
import Toast from 'src/components/Common/Toast/Toast';
import { ClinicState } from 'src/contexts/clinic/types';
import { SET_SSB_INFO, SSBActionTypes, SSBFlowState } from 'src/contexts/ssb/types';
import { capitalizeToKebabCase } from 'src/utils/array';
import { getEffectiveProgramType } from 'src/utils/programType';
import { getPreviousAppointmentByRules } from './fetchStatusAction';

export const skipStepAction = async ({
  context,
  setters,
  setState,
}: {
  context: {
    stepsRef: React.MutableRefObject<Step[] | null>;
    scheduledAppointments: any;
    user: User;
    clinic: ClinicState;
    appointmentsSchedulerRef: any;
    routePath: string;
    selectedLanguage: any;
  };
  setters: {
    setSelectedLanguage: React.Dispatch<React.SetStateAction<any>>;
    setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedLocation: React.Dispatch<React.SetStateAction<any>>;
    setAvailableProviders: React.Dispatch<React.SetStateAction<any>>;
    setSelectedTimeSlotKey: React.Dispatch<React.SetStateAction<string | null>>;
    setThereIsNoAvailability: React.Dispatch<React.SetStateAction<boolean>>;
  };
  setState: Dispatch<SSBActionTypes>;
}) => {
  const { scheduledAppointments, stepsRef, selectedLanguage, appointmentsSchedulerRef, routePath } =
    context;

  const currentStep = stepsRef.current?.find((s) => s.active);

  if (!currentStep) {
    return Toast.notification('error', 'No active step to skip');
  }

  if (!currentStep.config.skippable) {
    return Toast.notification('error', 'This step cannot be skipped');
  }

  setters.setIsSubmitting(true);

  MixpanelClient.trackEvent({
    event: MixpanelEvent.InputData,
    properties: {
      field: 'Step skipped',
      value: currentStep.details.specialtyName,
      source: routePath,
    },
  });

  const skipStep = await scheduleClient.skipSchedulingStep({
    memberId: context.user!.id,
    workflow: SchedulingWorkflow.SelfServeBooking,
    position: currentStep.position,
  });

  if (!skipStep || !skipStep.success) {
    setters.setIsSubmitting(false);
    return Toast.notification(
      'error',
      skipStep?.message || 'Failed to skip this step. Please try again.'
    );
  }

  // Fetch updated steps from the server
  const fetchedSteps = await scheduleClient.getStepsForMember({
    clinicId: context.clinic!.details.clinicId,
    memberId: context.user!.id,
    programType: getEffectiveProgramType({ user: context.user, clinic: context.clinic }) as
      | ProgramType.InClinic
      | ProgramType.Remote,
    systemSource: capitalizeToKebabCase(context.user!.systemSource) as LeadSystemSource,
    schedulingWorkflow: SchedulingWorkflow.SelfServeBooking,
  });

  if (!fetchedSteps) {
    setters.setIsSubmitting(false);
    return Toast.notification(
      'error',
      'There was an error obtaining the information. Please reload the page or wait for a support member to contact you, we are looking for the solution.'
    );
  }

  // Mark the current step as skipped and find next active step
  const updatedSteps = fetchedSteps.map((step) => {
    if (step.position === currentStep.position) {
      return { ...step, skipped: true, active: false };
    }
    return step;
  });

  const nextPendingStep = updatedSteps.find(
    (step) => !step.scheduled && !step.skipped && step.position > currentStep.position
  );

  if (nextPendingStep) {
    const finalSteps = updatedSteps.map((step) => ({
      ...step,
      active: step.position === nextPendingStep.position,
    }));
    context.stepsRef.current = finalSteps;
  } else {
    context.stepsRef.current = updatedSteps;
  }

  setters.setAvailableProviders(null);
  setters.setSelectedTimeSlotKey(null);
  setters.setSelectedLocation(undefined);
  setters.setIsSubmitting(false);
  setters.setSelectedLanguage(null);
  setters.setThereIsNoAvailability(false);

  // Check if all steps are now completed (scheduled or skipped)
  const allStepsCompleted = context.stepsRef.current?.every(
    (step) => step.scheduled || step.skipped
  );

  if (allStepsCompleted) {
    setState({ type: SET_SSB_INFO, payload: SSBFlowState.AllScheduled });
    return;
  }

  const previousAppointment = getPreviousAppointmentByRules({
    scheduledAppointments,
    stepsRef,
    selectedLanguage,
    calculatedSteps: context.stepsRef.current,
  });

  appointmentsSchedulerRef.current?.resetCalendar(previousAppointment);

  setState({ type: SET_SSB_INFO, payload: SSBFlowState.InitSchedulingProcess });
};
